/**
 * Email an announcement to the organisations it already targets (Trello #395).
 *
 * Marcus asked for the in-app notification pop-up to be mailable, because
 * brokers who don't log in often never see it. The organisation targeting
 * already exists on the notification — this dialog is the "draft the email and
 * send it" half.
 *
 * ── Why the preview is fetched rather than composed here ─────────────────────
 * The backend composes the body (branding, CTA resolution, escaping) and this
 * dialog renders exactly what it returns. Re-composing client-side would be a
 * second implementation of the same wording that could drift from the one that
 * actually goes out, which is the worst possible thing to be wrong about in a
 * preview.
 *
 * ── Why the body renders in a sandboxed iframe ───────────────────────────────
 * The preview is email HTML. `dangerouslySetInnerHTML` would (a) let the
 * message's styles leak into the admin app and (b) execute whatever the body
 * contains. A `sandbox=""` iframe has scripts, forms and navigation all
 * disabled, and renders the mail in its own document — closer to what the
 * recipient sees, and inert.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { adminApi, Notification, NotificationEmailPreview, Organization } from '../services/api';
import {
  ORG_AUDIENCE_ALL_LABEL,
  ORG_AUDIENCE_PAYING_LABEL,
  ORG_AUDIENCE_SELECTED_LABEL,
} from '../utils/org-payment-signal';

/** Typed into the confirm box before an all-organisations blast may be sent. */
export const CONFIRM_PHRASE = 'SEND';

/**
 * Matches the notification's own message cap on the backend. Capping the editor
 * at the same number means an admin cannot compose something the send would
 * then reject — a 400 after writing 2,000 characters is a bad way to find out.
 */
export const MAX_MESSAGE_LENGTH = 2000;

/**
 * A send to EVERY organisation reaches the entire customer base and cannot be
 * recalled, so it asks for a typed confirmation. A send to a named list is
 * already bounded by a list the admin picked, and gets the ordinary button.
 */
export const requiresTypedConfirmation = (allOrganizations: boolean): boolean => allOrganizations;

/**
 * Who this announcement is addressed to. ONE value, three modes — not a boolean
 * plus a mode, which is how two flags drift into a state that means neither.
 */
export type AnnouncementAudience = 'all' | 'paying' | 'selected';

/**
 * Turn the dialog's audience state into the fields that go on the wire.
 *
 * ONE place, called by the preview, the send AND the reload guard, so those
 * three cannot disagree about what was asked for. The guard comparing against a
 * differently-derived request is exactly how a dialog ends up re-previewing
 * forever.
 */
export const audienceRequest = (
  audience: AnnouncementAudience,
  selectedOrgIds: string[],
): { payingOrganizationsOnly: boolean; organizationIds: string[] } => ({
  payingOrganizationsOnly: audience === 'paying',
  organizationIds: audience === 'selected' ? selectedOrgIds : [],
});

/**
 * Does the backend we are talking to offer the "Selected organisations" option?
 * (Trello #395)
 *
 * Read off the ECHO, exactly as `supportsPayingOrganisationsFilter` below is —
 * and for the same reason. This app deploys independently of the backend, and
 * the option has its own server-side kill-switch, so admin-ahead-of-backend and
 * switched-off-underneath-a-loaded-tab are both normal. In either case the
 * preview simply omits the field, and offering a picker the server will ignore
 * is worse than not offering one: the admin narrows to three organisations, the
 * count does not move, and the mail goes to everyone the notification targets.
 *
 * `undefined` is the only "unsupported" value; `[]` means supported-and-empty.
 */
export const supportsSelectedOrganisationsFilter = (
  preview: NotificationEmailPreview | null,
): boolean => preview?.selectedOrganizationIds !== undefined;

/**
 * Are these the same selection? Order- and duplicate-insensitive.
 *
 * The server normalises the override (it trims, dedupes and drops empties), so
 * comparing the raw arrays would report a mismatch for a response that is in
 * fact exactly what we asked for — and the reload guard would re-fire forever.
 */
export const sameOrgSelection = (a: string[], b: string[]): boolean => {
  const left = Array.from(new Set(a)).sort();
  const right = Array.from(new Set(b)).sort();
  return left.length === right.length && left.every((id, i) => id === right[i]);
};

/**
 * The organisations the picker may offer: the IN-SCOPE set.
 *
 * The server intersects any selection with the notification's own targeting and
 * never widens it. Offering an out-of-scope organisation here would let an
 * admin tick something that silently resolves to nothing — so the picker offers
 * exactly what can be reached, and the intersection becomes invisible rather
 * than surprising.
 *
 * Reads `preview.organizationIds` — the notification's targeting — deliberately,
 * NOT `effectiveOrganizationIds`. Narrowing the options to the current
 * selection would make an unticked organisation impossible to tick again.
 *
 * An id with no matching row still becomes an option, labelled by its id, the
 * same fallback `organizationLabel` already makes: a targeted organisation
 * missing from the list is a reason to show it plainly, not to make the
 * announcement unsendable.
 */
export const selectableOrganizations = (
  preview: NotificationEmailPreview | null,
  organizations: Organization[],
): Organization[] => {
  if (!preview) return [];
  const targeted = preview.organizationIds || [];
  if (targeted.length === 0) return organizations;
  const byId = new Map(organizations.map((o) => [o.id, o]));
  return targeted.map((id) => byId.get(id) || ({ id, name: id } as Organization));
};

/** Plain-English "who is about to receive this", for the dialog's lead line. */
export const recipientSummary = (opts: {
  allOrganizations: boolean;
  organizationCount: number;
  /**
   * How many people will actually receive it. `undefined` means the backend did
   * not say (it predates the field) — the summary then reads exactly as it
   * always did, rather than inventing a number.
   */
  recipientCount?: number;
  /** `recipientCount` is a floor, not an exact figure. */
  recipientCountCapped?: boolean;
  /**
   * Which audience this send is addressed to (Trello #395). The scope wording
   * has to say so: "every registered user in EVERY organisation" beside a
   * narrowed count is a sentence the admin would reasonably act on and which is
   * not true of what will be sent.
   *
   * ONE value rather than a flag per mode, so the wording cannot describe a
   * combination the dialog cannot be in.
   */
  audience?: AnnouncementAudience;
}): string => {
  const scope =
    opts.audience === 'paying'
      ? opts.allOrganizations
        ? 'every registered user in every PAYING organisation'
        : `every registered user in the PAYING organisations among ${opts.organizationCount} targeted organisation${opts.organizationCount === 1 ? '' : 's'}`
      : opts.audience === 'selected'
        ? `every registered user in ${opts.organizationCount} selected organisation${opts.organizationCount === 1 ? '' : 's'}`
        : opts.allOrganizations
          ? 'every registered user in EVERY organisation'
          : `every registered user in ${opts.organizationCount} organisation${opts.organizationCount === 1 ? '' : 's'}`;

  if (opts.recipientCount === undefined) return scope;

  // The COUNT leads, because it is the fact an admin cannot get any other way.
  // The organisation scope reads as an audience and is not one: an announcement
  // can target an organisation that contains nobody who will be mailed, which is
  // exactly how #395 shipped two "sent" announcements that reached zero inboxes.
  if (opts.recipientCount === 0) return `NOBODY — no registered users match, so ${scope} means 0 people`;

  const people = `${opts.recipientCountCapped ? 'at least ' : ''}${opts.recipientCount} ${opts.recipientCount === 1 ? 'person' : 'people'}`;
  return `${people} — ${scope}`;
};

/**
 * Whether the Send button is armed. Every gate that could let an unintended
 * blast out lives here as a pure function so each one is directly testable —
 * this is the last thing standing between a click and a customer's inbox.
 */
export const canSend = (state: {
  loading: boolean;
  sending: boolean;
  preview: NotificationEmailPreview | null;
  subject: string;
  confirmText: string;
  resendAcknowledged: boolean;
  /**
   * A recipient count is in flight after an audience-filter change (#395).
   *
   * Disarming matters because the number on screen belongs to the PREVIOUS
   * audience until the new one lands. Sending in that window is the one thing
   * this dialog exists to prevent — a blast made against a figure the admin was
   * shown but which does not describe what goes out.
   */
  audienceRefreshing?: boolean;
  /** Which audience the admin has chosen (Trello #395). */
  audience?: AnnouncementAudience;
  /** How many organisations are ticked, when the audience is 'selected'. */
  selectedCount?: number;
}): boolean => {
  if (state.loading || state.sending || !state.preview) return false;
  if (state.audienceRefreshing) return false;
  if (!state.subject.trim()) return false;
  // "Selected organisations" with nothing ticked is nobody. The server refuses
  // it with a 400 either way; disarming here means the admin sees WHY before
  // clicking rather than after (Trello #395).
  if (state.audience === 'selected' && !state.selectedCount) return false;
  // Nobody would receive it, so there is nothing to send. The server refuses
  // this with a 400 either way; disarming the button here means the admin sees
  // WHY before clicking rather than after (Trello #395).
  //
  // `=== 0`, deliberately — not falsy. `undefined` is an older backend that
  // cannot report a count, and treating that as zero would disarm Send for
  // every announcement during a deploy window.
  if (state.preview.recipientCount === 0) return false;
  if (state.preview.alreadySentAt && !state.resendAcknowledged) return false;
  if (requiresTypedConfirmation(state.preview.allOrganizations)) {
    // Trimmed on purpose. The gate is "did you deliberately type this word",
    // not "can you avoid a trailing space" — a stray space from a paste
    // silently disarming the button teaches people to mash it, which is worse
    // for a confirmation than accepting ' SEND '. Case is NOT normalised, so
    // 'send' still fails.
    return state.confirmText.trim() === CONFIRM_PHRASE;
  }
  return true;
};

/** Prefer the org's display name, falling back to the id the backend targets. */
export const organizationLabel = (id: string, organizations: Organization[]): string => {
  const match = organizations.find((o) => o.id === id);
  return match?.displayName || match?.name || id;
};

/**
 * Does the backend we are talking to actually support the paying-organisations
 * filter? (Trello #395)
 *
 * Read off the ECHO, not off a build flag. This app deploys independently of the
 * backend, so admin-ahead-of-backend is a normal window — and the filter also
 * has a server-side kill-switch, which can turn it off underneath a dashboard
 * that is already loaded. In both cases the preview simply omits the field.
 *
 * Offering a control the server will ignore is worse than not offering it: the
 * admin picks "Paying organisations", the count does not move, and the mail goes
 * to everyone. So absence hides the control entirely — the same shape as
 * `hasBillingSignalData` on the Bulk Invoices page, which exists because a
 * frontend that shipped ahead of its backend once filtered a 113-org table down
 * to nothing and read as "we have no customers".
 *
 * `undefined` is the only "unsupported" value; `false` means supported-and-off.
 */
export const supportsPayingOrganisationsFilter = (
  preview: NotificationEmailPreview | null,
): boolean => preview?.payingOrganizationsOnly !== undefined;

interface NotificationEmailDialogProps {
  open: boolean;
  notification: Notification | null;
  organizations?: Organization[];
  onClose: () => void;
  /** Called after a successful send so the caller can refresh + snackbar. */
  onSent: (message: string) => void;
}

const NotificationEmailDialog: React.FC<NotificationEmailDialogProps> = ({
  open,
  notification,
  organizations = [],
  onClose,
  onSent,
}) => {
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<NotificationEmailPreview | null>(null);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [previewing, setPreviewing] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [resendAcknowledged, setResendAcknowledged] = useState(false);
  /** Who this announcement is addressed to (Trello #395). 'all' = today's behaviour. */
  const [audience, setAudience] = useState<AnnouncementAudience>('all');
  /** The ticked organisations, updated on every click so the chips stay live. */
  const [selectedOrgIds, setSelectedOrgIds] = useState<string[]>([]);
  /**
   * The selection the preview has actually been asked for — `selectedOrgIds`
   * on a short debounce.
   *
   * Ticking five organisations would otherwise fire five counted reloads, and
   * each one walks the user-profiles table. The Autocomplete writes the live
   * value so the chips respond instantly; only this settled one reaches the
   * server, and Send stays disarmed while the two differ.
   */
  const [committedSelection, setCommittedSelection] = useState<string[]>([]);
  /** A recipient count is in flight after a filter change — Send stays disarmed. */
  const [audienceRefreshing, setAudienceRefreshing] = useState(false);

  /**
   * Monotonic request id. An in-flight preview for a PREVIOUS notification can
   * resolve after the dialog has been reopened on a different one, and would
   * then overwrite the current preview — showing one announcement's recipients
   * and body under another's identity, with Send armed. For an unrecallable
   * blast that is the worst possible confusion, so a late response is dropped
   * rather than applied.
   */
  const requestSeq = useRef(0);

  /**
   * Which request currently owns `audienceRefreshing` (Trello #395).
   *
   * Only the request that RAISED the flag may lower it — see the finally block
   * in `loadPreview`. A plain boolean strands itself when a debounced typing
   * reload overtakes the toggle's counted reload.
   */
  const audienceRefreshSeq = useRef(0);

  /**
   * `initial`, `withRecipientCount` and "seed the editor" used to be ONE flag
   * (`editedMessage === undefined`). Trello #395 needs them apart: toggling the
   * audience filter must re-ask for the COUNT — the number is a property of the
   * audience, not of the wording — while leaving the admin's typed edits alone
   * and not blanking the dialog under the cursor.
   */
  const loadPreview = useCallback(async (
    id: string,
    opts: {
      editedMessage?: string;
      /** First load for this notification: blank the dialog and seed the editor. */
      initial?: boolean;
      withRecipientCount?: boolean;
      payingOrganizationsOnly?: boolean;
      organizationIds?: string[];
    } = {},
  ) => {
    const seq = ++requestSeq.current;
    const isCurrent = () => requestSeq.current === seq;

    const { editedMessage, initial = false, withRecipientCount = false } = opts;
    const payingOnly = opts.payingOrganizationsOnly ?? false;
    const organizationIds = opts.organizationIds ?? [];

    // The FIRST load blanks the dialog; a re-preview while typing must not, or
    // the editor would flicker out from under the cursor on every keystroke.
    if (initial) setLoading(true);
    else setPreviewing(true);
    // A count in flight disarms Send (see canSend): until it lands, the number
    // on screen describes the audience the admin just switched AWAY from.
    //
    // OWNED BY THIS REQUEST'S SEQUENCE, not by a bare boolean. A debounced
    // typing reload can overtake the toggle's counted reload — the toggle then
    // finishes stale and returns early below, while the typing reload declines
    // to clear a flag it never set. The flag would stay true and Send would be
    // disarmed until the dialog was closed and reopened. Recording the owning
    // seq lets the request that RAISED it always be the one that lowers it,
    // current or not.
    if (withRecipientCount && !initial) {
      audienceRefreshSeq.current = seq;
      setAudienceRefreshing(true);
    }
    setError(null);
    try {
      const result = await adminApi.previewNotificationEmail(id, {
        message: editedMessage,
        withRecipientCount,
        payingOrganizationsOnly: payingOnly,
        organizationIds,
      });
      if (!isCurrent()) return;
      setPreview((previous) =>
        // Carry the count forward across debounced re-previews, which do not
        // return one. Letting it fall back to `undefined` would make the
        // summary drop the audience line mid-edit and — worse — re-arm Send on
        // an announcement that reaches nobody, since the zero gate reads the
        // count off the preview.
        //
        // Gated on `!withRecipientCount`, NOT on `!initial`: a reload that ASKED
        // for a count and did not get one must show no number rather than the
        // previous audience's. Carrying it there would put the all-organisations
        // figure beside a paying-only Send button — a confidently wrong count,
        // which is the failure this dialog was rebuilt to remove.
        result && !withRecipientCount && result.recipientCount === undefined && previous
          ? {
              ...result,
              ...(previous.recipientCount !== undefined && {
                recipientCount: previous.recipientCount,
                recipientCountCapped: previous.recipientCountCapped,
              }),
            }
          : result,
      );
      if (initial) {
        setSubject(result?.subject || '');
        setMessage(result?.message || '');
      }
    } catch (err: any) {
      if (!isCurrent()) return;
      // A failed RE-preview keeps the last good one on screen: the admin's
      // typing is not lost, and the send is still gated on what they can see.
      if (initial) setPreview(null);
      // ...but a failed COUNTED reload must not leave the previous audience's
      // number on screen. The selection has already advanced, so `selectionDirty`
      // is false and `audienceRefreshing` is cleared in `finally` — Send would
      // re-arm beside a figure belonging to the audience the admin just moved
      // away from. Dropping the count falls back to the scope wording with Send
      // still available, which is the honest state; showing the old number is
      // the confidently-wrong count this dialog exists to remove.
      else if (withRecipientCount) {
        setPreview((p) =>
          p ? { ...p, recipientCount: undefined, recipientCountCapped: undefined } : p,
        );
      }
      setError(err.response?.data?.message || err.message || 'Failed to load the email preview');
    } finally {
      // BEFORE the isCurrent() guard, deliberately. A stale counted reload still
      // has to release the flag it raised, or an overtaken toggle leaves Send
      // disarmed for the life of the dialog.
      if (audienceRefreshSeq.current === seq) setAudienceRefreshing(false);
      if (!isCurrent()) return;
      if (initial) setLoading(false);
      else setPreviewing(false);
    }
  }, []);

  useEffect(() => {
    if (!open || !notification) return;
    // Reset every time the dialog opens so a previous notification's typed
    // confirmation can never carry over and arm a different send.
    setConfirmText('');
    setResendAcknowledged(false);
    setPreview(null);
    setSubject('');
    setMessage('');
    // Back to All organisations on every open. A previous notification's
    // audience filter carrying over would narrow a send the admin never chose to
    // narrow — and they would have no reason to look, because the control reads
    // as a fresh dialog. The organisation selection carries the same risk and is
    // cleared with it — worse, in fact: a stale tick list would name
    // organisations this notification may not even target.
    setAudience('all');
    setSelectedOrgIds([]);
    setCommittedSelection([]);
    setAudienceRefreshing(false);
    loadPreview(notification.id, { initial: true, withRecipientCount: true });
  }, [open, notification, loadPreview]);

  /**
   * Re-resolve the preview when the audience filter changes (Trello #395).
   *
   * WITH the count, unlike the debounced typing reload below: the number of
   * people is a property of the audience, so a toggle invalidates it. Skipped on
   * the first render for each open, because the effect above already loaded it.
   */
  useEffect(() => {
    if (!open || !notification || !preview) return;
    // Compare BOTH halves of the request against BOTH halves of the echo, and
    // build the request the same way handleSend does — a guard that derives it
    // differently is how this loop fails to terminate.
    //
    // The selection is compared PRE-intersection (`selectedOrganizationIds`,
    // not `effectiveOrganizationIds`): the server drops ids the notification
    // does not target, so comparing against the resolved audience would
    // mismatch on every response for a selection containing one, and
    // re-preview forever.
    const asked = audienceRequest(audience, committedSelection);
    if (
      asked.payingOrganizationsOnly === (preview.payingOrganizationsOnly ?? false) &&
      sameOrgSelection(asked.organizationIds, preview.selectedOrganizationIds ?? [])
    ) {
      return;
    }

    // Drop the count BEFORE the reload. It belongs to the audience the admin
    // just switched away from, and a debounced typing reload firing in the
    // window would otherwise carry it forward and display it beside the new
    // audience. Showing no number until the real one lands is the honest state;
    // showing the previous audience's is the confidently-wrong count.
    setPreview((p) =>
      p ? { ...p, recipientCount: undefined, recipientCountCapped: undefined } : p,
    );

    loadPreview(notification.id, {
      editedMessage: message,
      withRecipientCount: true,
      payingOrganizationsOnly: asked.payingOrganizationsOnly,
      organizationIds: asked.organizationIds,
    });
    // `preview` and `message` are read, not depended on: including `preview`
    // would re-fire on the response this produced, and including `message` would
    // turn every keystroke into a counted reload.
    //
    // `committedSelection` is the DEBOUNCED selection, not the live one — this
    // effect must not fire once per tick while the admin is still choosing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audience, committedSelection, open, notification, loadPreview]);

  /**
   * A chosen mode whose server switch has gone away resets to All.
   *
   * The kill-switches can flip under a dashboard that is already open, and the
   * radio would then hold a value with no matching option — while handleSend
   * silently omits the field, so the send widens to everyone the notification
   * targets with nothing on screen saying so.
   */
  useEffect(() => {
    if (!preview) return;
    if (audience === 'paying' && !supportsPayingOrganisationsFilter(preview)) setAudience('all');
    if (audience === 'selected' && !supportsSelectedOrganisationsFilter(preview)) setAudience('all');
  }, [preview, audience]);

  /**
   * Settle the live selection into the one the preview is asked for.
   *
   * Ticking organisations is a rapid sequence of clicks and each counted reload
   * walks the user-profiles table, so the request waits for the admin to stop.
   * Send stays disarmed across the gap (see `selectionDirty` below) — the count
   * on screen belongs to the previous selection until the new one lands, and
   * sending against a number that does not describe what goes out is the single
   * thing this dialog exists to prevent.
   */
  useEffect(() => {
    if (sameOrgSelection(selectedOrgIds, committedSelection)) return;
    const timer = window.setTimeout(() => setCommittedSelection(selectedOrgIds), 400);
    return () => window.clearTimeout(timer);
  }, [selectedOrgIds, committedSelection]);

  /**
   * Re-render the preview from the edited wording, debounced.
   *
   * Debounced rather than per-keystroke because each render is a round trip;
   * and re-composed on the SERVER rather than in this component because the
   * preview's whole job is to be exactly what goes out. A client-side
   * approximation of the composer would be a second implementation of the
   * wording, drifting the moment either changes.
   */
  useEffect(() => {
    if (!open || !notification || !preview) return;
    // `message` is seeded FROM the preview, so this fires only once the admin
    // has actually changed something.
    if (message === (preview.message || '')) return;

    const timer = window.setTimeout(
      () =>
        loadPreview(notification.id, {
          editedMessage: message,
          // No count: the wording does not change who receives it, and asking
          // per keystroke would re-walk the user-profiles table each time. The
          // audience toggle above is what re-counts.
          withRecipientCount: false,
          // Forward the WHOLE audience, not just the flag: a re-preview while
          // narrowed must not quietly widen the send back to everyone.
          ...audienceRequest(audience, committedSelection),
        }),
      500,
    );
    return () => window.clearTimeout(timer);
    // `preview` is deliberately absent: including it would re-arm the timer with
    // the response this effect just produced, and re-preview forever.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message, open, notification, loadPreview]);

  const handleSend = async () => {
    if (!notification || !preview) return;
    setSending(true);
    setError(null);
    try {
      const response = await adminApi.sendNotificationEmail(notification.id, {
        subject,
        // Send the edited WORDING, not the rendered HTML: the server composes it
        // with the same function the preview used, so what goes out cannot
        // differ from what they just read.
        message,
        resend: Boolean(preview.alreadySentAt),
        // `undefined`, not `false`, when the backend has not advertised the
        // filter: JSON.stringify drops an undefined value, so the key never
        // reaches a server that would reject it as unknown (Trello #395).
        payingOrganizationsOnly: supportsPayingOrganisationsFilter(preview)
          ? audience === 'paying'
          : undefined,
        // `undefined`, never `[]` — an empty array is a value the server would
        // try to resolve, while an absent key is dropped by JSON.stringify and
        // never reaches a backend that would reject it as unknown.
        organizationIds:
          supportsSelectedOrganisationsFilter(preview) && audience === 'selected' && selectedOrgIds.length > 0
            ? selectedOrgIds
            : undefined,
      });
      onSent(response?.data?.message || 'Announcement email queued.');
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to send the announcement email');
    } finally {
      setSending(false);
    }
  };

  // The live selection has not reached the server yet, so the count on screen
  // still describes the previous one. Folded into the same flag the counted
  // reload uses, for the reason canSend's docblock already gives.
  const selectionDirty = !sameOrgSelection(selectedOrgIds, committedSelection);
  const armed = canSend({
    loading,
    sending,
    preview,
    subject,
    confirmText,
    resendAcknowledged,
    audienceRefreshing: audienceRefreshing || selectionDirty,
    audience,
    selectedCount: selectedOrgIds.length,
  });
  const payingFilterAvailable = supportsPayingOrganisationsFilter(preview);
  const selectedFilterAvailable = supportsSelectedOrganisationsFilter(preview);
  // The two options ship behind INDEPENDENT server switches, so either may be
  // absent. Show the control if at least one narrowing exists to offer.
  const audienceFilterAvailable = payingFilterAvailable || selectedFilterAvailable;
  const organizationOptions = selectableOrganizations(preview, organizations);
  // The audience the recipient count was resolved under. Falls back to the
  // notification's own targeting on a backend that does not echo it, which is
  // exactly what the count means there.
  const effectiveOrganizationIds = preview?.effectiveOrganizationIds ?? preview?.organizationIds ?? [];

  return (
    <Dialog open={open} onClose={sending ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Email this announcement</DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }} data-testid="email-preview-loading">
            <CircularProgress size={28} />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} data-testid="email-dialog-error">
            {error}
          </Alert>
        )}

        {preview && (
          <>
            <Alert
              severity={
                preview.recipientCount === 0
                  ? 'error'
                  : preview.allOrganizations
                    ? 'warning'
                    : 'info'
              }
              sx={{ mb: 2 }}
              data-testid="email-recipient-summary"
            >
              This will email{' '}
              <strong>
                {recipientSummary({
                  allOrganizations: preview.allOrganizations,
                  // The EFFECTIVE list, not the notification's targeting: under
                  // a narrowing those differ, and the wording must describe what
                  // is about to be sent.
                  organizationCount: effectiveOrganizationIds.length,
                  recipientCount: preview.recipientCount,
                  recipientCountCapped: preview.recipientCountCapped,
                  audience,
                })}
              </strong>
              {preview.recipientCount === 0
                ? audience === 'paying'
                  ? '. No paying organisation here has a registered user — switch to All organisations, or check the Billing column on the Organizations page.'
                  : audience === 'selected'
                    ? '. None of the selected organisations has a registered user — widen the selection, or switch to All organisations.'
                    : '. Check the organisation has members and that their accounts are enabled.'
                : '. It cannot be recalled.'}
            </Alert>

            {audienceFilterAvailable && (
              <FormControl sx={{ mb: 2 }} data-testid="email-audience-filter">
                <FormLabel id="email-audience-filter-label" sx={{ fontSize: '0.875rem' }}>
                  Send to
                </FormLabel>
                <RadioGroup
                  row
                  aria-labelledby="email-audience-filter-label"
                  value={audience}
                  onChange={(e) => setAudience(e.target.value as AnnouncementAudience)}
                >
                  {/* Radios rather than a switch or a dropdown: a switch would
                      hide "All organisations" as an implicit default, and on a
                      screen whose job is to make an unrecallable blast legible
                      every option should be on show. Each narrowing is rendered
                      only when its own server switch says so — offering one the
                      backend will ignore is worse than not offering it. */}
                  <FormControlLabel
                    value="all"
                    control={<Radio size="small" disabled={sending} />}
                    label={ORG_AUDIENCE_ALL_LABEL}
                  />
                  {payingFilterAvailable && (
                    <FormControlLabel
                      value="paying"
                      control={<Radio size="small" disabled={sending} />}
                      label={ORG_AUDIENCE_PAYING_LABEL}
                    />
                  )}
                  {selectedFilterAvailable && (
                    <FormControlLabel
                      value="selected"
                      control={<Radio size="small" disabled={sending} />}
                      label={ORG_AUDIENCE_SELECTED_LABEL}
                    />
                  )}
                </RadioGroup>
                <FormHelperText>
                  {audience === 'paying'
                    ? 'Organisations on a paid subscription, direct EFT, or a managed plan — the same set the Organizations page marks Paid or Managed. It still only reaches the organisations this notification targets.'
                    : audience === 'selected'
                      ? 'Only the organisations you tick below. The list is the organisations this notification targets, so a selection can narrow the send but never widen it.'
                      : 'Everyone in the organisations this notification targets, paying or not.'}
                </FormHelperText>
              </FormControl>
            )}

            {audience === 'selected' && (
              // The testid goes on this Box, not on the Autocomplete: MUI
              // forwards unknown props, but wrapping it here keeps the hook
              // stable if the control is ever swapped, and matches how the rest
              // of this dialog is addressed.
              <Box sx={{ mb: 2 }} data-testid="email-selected-organizations">
                <Autocomplete
                  multiple
                  size="small"
                  // The popup stays open across ticks. MUI closes it after each
                  // selection by default, which for a multi-select means
                  // reopening the list once per organisation — and this control
                  // exists precisely so an admin can pick several.
                  disableCloseOnSelect
                  disabled={sending}
                  options={organizationOptions}
                  // Built FROM the selection, not filtered from the options.
                  // Filtering silently drops any selected id the options no
                  // longer contain: the chip disappears while selectedOrgIds
                  // still holds it, so what is displayed and what would be sent
                  // diverge with nothing on screen saying so. The server
                  // intersects and never widens, so no wrong audience goes out —
                  // but a selection you cannot see is exactly the confidently-
                  // wrong state this dialog exists to remove.
                  value={selectedOrgIds.map(
                    (id) =>
                      organizationOptions.find((o) => o.id === id) || ({ id, name: id } as Organization),
                  )}
                  onChange={(_event, chosen) => setSelectedOrgIds(chosen.map((o) => o.id))}
                  getOptionLabel={(option) => organizationLabel(option.id, organizations)}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Organisations"
                      placeholder={selectedOrgIds.length === 0 ? 'Pick one or more' : ''}
                      helperText={
                        selectedOrgIds.length === 0
                          ? 'Tick at least one organisation to enable Send.'
                          : selectionDirty
                            ? 'Updating the recipient count…'
                            : `${selectedOrgIds.length} of ${organizationOptions.length} selected.`
                      }
                    />
                  )}
                />
              </Box>
            )}

            {preview.alreadySentAt && (
              <Alert severity="warning" sx={{ mb: 2 }} data-testid="already-sent-warning">
                <Typography variant="body2" sx={{ mb: 1 }}>
                  This announcement was already emailed on{' '}
                  {new Date(preview.alreadySentAt).toLocaleString()}.
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={resendAcknowledged}
                      onChange={(e) => setResendAcknowledged(e.target.checked)}
                      slotProps={{ input: { 'aria-label': 'Send it again anyway' } }}
                    />
                  }
                  label="Send it again anyway"
                />
              </Alert>
            )}

            {!preview.allOrganizations && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }} data-testid="email-target-orgs">
                {/* Who it REACHES, which under a narrowing is not the same as
                    what the notification targets. Falls back to the targeting on
                    a backend that does not echo the effective list. */}
                {effectiveOrganizationIds.map((id) => (
                  <Chip key={id} size="small" label={organizationLabel(id, organizations)} />
                ))}
              </Box>
            )}

            <TextField
              fullWidth
              label="Subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              sx={{ mb: 2 }}
              inputProps={{ 'data-testid': 'email-subject-input', maxLength: 200 }}
            />

            <TextField
              fullWidth
              multiline
              minRows={4}
              label="Message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              sx={{ mb: 2 }}
              helperText={
                previewing
                  ? 'Updating the preview…'
                  : 'Edit the wording before you send. The preview below updates as you type.'
              }
              inputProps={{ 'data-testid': 'email-message-input', maxLength: MAX_MESSAGE_LENGTH }}
            />

            <Typography variant="caption" color="text.secondary">
              Preview
            </Typography>
            <Box
              component="iframe"
              title="Announcement email preview"
              data-testid="email-body-preview"
              // Sandboxed with NO allow-* tokens: scripts, forms and navigation
              // are all disabled, so email HTML renders inertly and its styles
              // stay out of the admin app.
              sandbox=""
              srcDoc={preview.bodyHtml}
              sx={{
                width: '100%',
                // Tall enough to show a short announcement whole — header bar,
                // copy, CTA and sign-off — without scrolling. `bodyHtml` became
                // the FULL branded document on 2026-08-19; at the previous 320
                // the layout's header alone pushed the CTA out of view, and a
                // preview whose point is "recognise this before you send it"
                // should not need scrolling to recognise.
                height: 520,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                // The layout paints its own page colour; this only shows while
                // the document loads.
                backgroundColor: '#ffffff',
              }}
            />

            {requiresTypedConfirmation(preview.allOrganizations) && (
              <TextField
                fullWidth
                sx={{ mt: 2 }}
                label={`Type ${CONFIRM_PHRASE} to confirm`}
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                helperText="This goes to every organisation on the platform."
                inputProps={{ 'data-testid': 'email-confirm-input' }}
              />
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={sending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color={preview?.allOrganizations ? 'warning' : 'primary'}
          onClick={handleSend}
          disabled={!armed}
          data-testid="email-send-button"
          startIcon={sending ? <CircularProgress size={18} /> : null}
        >
          {sending ? 'Sending…' : 'Send email'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NotificationEmailDialog;
